import { useEffect, useState } from 'react';
import { Bell, UserPlus, Flag, Radio, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { api } from '../../lib/supabase';
import { useAuth } from '../../lib/auth-context';

const POLL_INTERVAL_MS = 60_000; // 60s

/**
 * NotificationBell — header bell icon + dropdown panel.
 *
 * Aggregates three streams (see api.getNotifications):
 *   • Incoming friend requests
 *   • Events you joined starting within the next 1 hour
 *   • Events you joined that have just gone live
 *
 * Behaviour:
 *   • Red dot when there's at least one notification
 *   • Auto-polls every 60s while the bell is mounted
 *   • Tapping an item navigates to its `link` and closes the popover
 */
export function NotificationBell() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  async function refresh() {
    if (!user?.id) return;
    setLoading(true);
    try {
      const list = await api.getNotifications();
      setItems(list || []);
    } catch {
      // Silent — header bell shouldn't surface toasts; just keep last state.
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!user?.id) {
      setItems([]);
      return;
    }
    refresh();
    const id = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const unread = items.length;

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) refresh(); }}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Notifications"
          data-testid="header-notifications"
          className="relative"
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              data-testid="header-notifications-dot"
              className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-primary"
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-80 p-0 max-h-[60vh] overflow-y-auto"
        data-testid="notifications-panel"
      >
        <div className="px-3 py-2 border-b border-border flex items-center justify-between">
          <div>
            <p className="rivl-label text-[10px]">Notifications</p>
            <p className="text-[10px] text-muted-foreground">{unread} new</p>
          </div>
          {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
        </div>

        {items.length === 0 ? (
          <div className="px-4 py-6 text-center" data-testid="notifications-empty">
            <Bell className="h-5 w-5 mx-auto text-muted-foreground/60" />
            <p className="mt-2 text-xs text-muted-foreground">You're all caught up.</p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {items.map((n) => (
              <li key={n.id} data-testid={`notification-${n.kind}-${n.id}`}>
                <Link
                  to={n.link}
                  onClick={() => setOpen(false)}
                  className="flex items-start gap-3 px-3 py-3 hover:bg-secondary/40 transition-colors"
                >
                  <NotificationIcon kind={n.kind} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold leading-tight">{n.title}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{n.body}</div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}

function NotificationIcon({ kind }) {
  const cls = 'h-7 w-7 rounded-md flex items-center justify-center flex-shrink-0';
  if (kind === 'friend_request') {
    return <div className={`${cls} bg-primary/10 text-primary`}><UserPlus className="h-3.5 w-3.5" /></div>;
  }
  if (kind === 'event_started') {
    return <div className={`${cls} bg-success/10 text-success`}><Radio className="h-3.5 w-3.5 animate-pulse" /></div>;
  }
  // event_starting_soon (default)
  return <div className={`${cls} bg-brand-blue/10 text-brand-blue`}><Flag className="h-3.5 w-3.5" /></div>;
}
